import { ReactNode, useEffect, useState } from "react";

import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import NotificationsContext from "@voxmedia/duet-context/context/notifications-context";
import { useDocumentVisibilityChange } from "@voxmedia/duet-ui/hooks/use-document-visibility-change";

import NotificationsRootStore from "./store/NotificationsRootStore";

interface Props {
  children?: ReactNode;
}

/**
 * Provider component for notifications context
 */
export const NotificationsProvider = ({ children }: Props) => {
  const { user, userLoading } = useAuth();
  const [rootStore, setRootStore] = useState<NotificationsRootStore | null>(
    null,
  );
  const isDocumentVisible = useDocumentVisibilityChange();

  useEffect(() => {
    const loadUser = async () => {
      if (userLoading || !user || !user.notificationsToken) {
        return;
      }

      const rootStore = new NotificationsRootStore(user);
      await rootStore.userStore.loadViewer();

      setRootStore(rootStore);
    };
    void loadUser();
  }, [user, user?.notificationsToken, userLoading]);

  // Once we have a viewer and the document is visible, start polling for notifications status.
  // This doesn't stop the poll when the document becomes hidden,
  // it just ensures it's polling when the document becomes visible again.
  useEffect(() => {
    if (!rootStore?.userStore.viewer || !isDocumentVisible) {
      return;
    }

    rootStore.notificationsStore.stopPollingNotificationsStatus(); // clears out old interval
    rootStore.notificationsStore.startPollingNotificationsStatus();
  }, [isDocumentVisible, rootStore]);

  return (
    <NotificationsContext.Provider
      value={{
        RootStore: rootStore,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
